import { z, type ZodTypeAny } from "zod";
import { env } from "@/env.js";

export type ApiError = {
  status: number;
  message: string;
  details?: unknown;
};

export class HttpError extends Error implements ApiError {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path, env.VITE_API_BASE_URL);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Authenticated fetch wrapper. The caller passes the access_token; we never read
 * from a global because the token lives in AuthProvider memory only.
 */
export async function apiFetch<TSchema extends ZodTypeAny>(
  path: string,
  accessToken: string,
  schema: TSchema,
  opts: RequestOptions = {},
): Promise<z.infer<TSchema>> {
  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // 204 No Content guard.
  if (res.status === 204) {
    return schema.parse(undefined as unknown);
  }

  const text = await res.text();
  let json: unknown = null;
  try { json = text ? JSON.parse(text) : null; } catch {
    throw new HttpError(res.status, "Invalid JSON response", { text });
  }

  if (!res.ok) {
    const errBody = json as { error?: { message?: string }; message?: string; missing?: unknown } | null;
    const msg = errBody?.error?.message ?? errBody?.message ?? `HTTP ${res.status}`;
    throw new HttpError(res.status, msg, errBody ?? undefined);
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new HttpError(500, `Response schema validation failed: ${parsed.error.message}`, json);
  }
  return parsed.data;
}
